/*
  Dhrohar bag drawer helper. Works on top of Dawn's cart-drawer.js / product-form.js; adds no cart requests.
  - After Dawn's product form adds an item (PUB_SUB_EVENTS.cartUpdate, source 'product-form'), Dawn re-renders
    #CartDrawer from the add response. This element then fills the "Added to your bag" panel from the matching
    line (image, title, options, quantity, fill badge/contents) and moves focus to "Continue shopping".
  - The panel hides again when the drawer closes, and the "(n)" count in the title follows Dawn's re-renders.
  Placed inside <cart-drawer> but outside #CartDrawer, so Dawn's re-renders never replace it.

  Bag page and drawer helpers (run once, also loaded by sections/main-cart-items.liquid):
  - Quantity errors: when Shopify answers /cart/change with a specific message (errors / description), that message
    replaces Dawn's generic error text on the line and in the bag error region (422 only). The response is untouched.
  - Focus after Remove on the bag page: next line's quantity, else the bag heading, else the empty-state heading.
*/
(() => {
  if (window.dhroharCartHelpers) return;
  window.dhroharCartHelpers = true;

  const genericError = () => (window.cartStrings && window.cartStrings.error) || '';
  const changeUrl = () => (window.routes && window.routes.cart_change_url) || '/cart/change';

  const pickMessage = (data) => {
    if (!data || typeof data !== 'object') return '';
    const errors = data.errors;
    if (typeof errors === 'string' && errors.trim()) return errors.trim();
    if (errors && typeof errors === 'object') {
      const text = Object.values(errors).flat().filter((v) => typeof v === 'string').join(' ');
      if (text.trim()) return text.trim();
    }
    if (typeof data.description === 'string' && data.description.trim()) return data.description.trim();
    if (typeof data.message === 'string' && data.message.trim() && data.message !== 'Cart Error') return data.message.trim();
    return '';
  };

  let lastError = null;
  const applyError = () => {
    if (!lastError || Date.now() - lastError.at > 3000) return;
    const { line, message } = lastError;
    if (line) {
      const lineError =
        document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const text = lineError && lineError.querySelector('.cart-item__error-text');
      if (text && text.textContent !== message) text.textContent = message;
    }
    ['cart-errors', 'CartDrawer-CartErrors'].forEach((id) => {
      const region = document.getElementById(id);
      if (region && region.textContent.trim() === genericError()) region.textContent = message;
    });
  };

  const nativeFetch = window.fetch.bind(window);
  window.fetch = function dhroharFetch(input, init) {
    const request = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : input && input.url;
    if (!url || !String(url).includes(changeUrl())) return request;
    let line = null;
    try {
      line = JSON.parse(init && init.body).line || null;
    } catch (error) {
      line = null;
    }
    return request.then((response) => {
      if (response.status === 422) {
        response
          .clone()
          .json()
          .then((data) => {
            const message = pickMessage(data);
            if (!message) return;
            lastError = { line, message, at: Date.now() };
            [0, 120, 400, 900].forEach((delay) => setTimeout(applyError, delay));
          })
          .catch(() => {});
      }
      return response;
    });
  };

  let lastRemove = null;
  document.addEventListener(
    'click',
    (event) => {
      const remove = event.target.closest && event.target.closest('cart-remove-button');
      if (!remove || !remove.closest('#main-cart-items')) return;
      lastRemove = { index: Number(remove.dataset.index), at: Date.now() };
    },
    true
  );

  const focusAfterRemove = () => {
    if (!lastRemove || Date.now() - lastRemove.at > 8000) return;
    const { index } = lastRemove;
    const active = document.activeElement;
    // Wait until Dawn has replaced the removed row (focus falls back to <body>); earlier mutations keep the request pending.
    if (active && active !== document.body && active.isConnected) return;
    lastRemove = null;
    const focusHeading = (heading) => {
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus();
    };
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || cartItems.classList.contains('is-empty') || !document.querySelector('#main-cart-items .cart-item')) {
      focusHeading(document.querySelector('cart-items .cart__empty-text'));
      return;
    }
    const nextRow = document.getElementById(`CartItem-${index}`);
    const input = nextRow && nextRow.querySelector('.quantity__input');
    if (input) input.focus();
    else focusHeading(document.querySelector('cart-items .title--primary'));
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'cart-items') return;
      setTimeout(focusAfterRemove, 0);
    });
  }
  // Also react to Dawn replacing the bag page lines (covers re-renders that do not publish).
  const watchCartPage = () => {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return;
    new MutationObserver(() => {
      if (lastRemove) setTimeout(focusAfterRemove, 0);
    }).observe(cartItems, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCartPage);
  else watchCartPage();
})();

(() => {
  if (customElements.get('dhrohar-bag-drawer')) return;

  customElements.define(
    'dhrohar-bag-drawer',
    class DhroharBagDrawer extends HTMLElement {
      connectedCallback() {
        this.drawer = this.closest('cart-drawer');
        this.container = this.drawer?.querySelector('#CartDrawer');
        if (!this.drawer || !this.container) return;

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // Same 'Added to your bag' panel for product-page adds and wishlist adds
            if (!event || !['product-form', 'dhrohar-wishlist', 'dhrohar-cart-picks'].includes(event.source)) return;
            const data = event.cartData || {};
            const line = data.key ? data : Array.isArray(data.items) ? data.items[0] : null;
            this.pending = {
              key: line?.key || '',
              variantId: String(event.productVariantId || line?.variant_id || line?.id || ''),
              at: Date.now(),
            };
          });
        }

        this.contentObserver = new MutationObserver((records) => this.onContentChange(records));
        this.contentObserver.observe(this.container, { childList: true, subtree: true });

        this.wasActive = this.drawer.classList.contains('active');
        this.stateObserver = new MutationObserver(() => {
          const active = this.drawer.classList.contains('active');
          if (this.wasActive && !active) this.hidePanel();
          this.wasActive = active;
        });
        this.stateObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.contentObserver?.disconnect();
        this.stateObserver?.disconnect();
      }

      onContentChange(records) {
        this.syncCount();
        if (!this.pending) return;
        const drawerReplaced = records.some((record) => record.target === this.container && record.addedNodes.length);
        if (!drawerReplaced) return;
        const pending = this.pending;
        this.pending = null;
        if (Date.now() - pending.at > 15000) return;
        this.showPanel(pending);
      }

      syncCount() {
        const items = this.container.querySelector('cart-drawer-items[data-dhrohar-item-count]');
        const count = this.container.querySelector('[data-dhrohar-bag-count]');
        if (!items || !count) return;
        const n = parseInt(items.dataset.dhroharItemCount, 10) || 0;
        const text = n > 0 ? ` (${n})` : '';
        if (count.textContent !== text) count.textContent = text;
      }

      findLine(pending) {
        const rows = Array.from(this.container.querySelectorAll('[data-dhrohar-line-key]'));
        return (
          (pending.key && rows.find((row) => row.dataset.dhroharLineKey === pending.key)) ||
          (pending.variantId && rows.find((row) => row.dataset.dhroharVariantId === pending.variantId)) ||
          null
        );
      }

      showPanel(pending) {
        const panel = this.container.querySelector('[data-dhrohar-bag-added]');
        const slot = panel?.querySelector('[data-dhrohar-bag-added-item]');
        if (!panel || !slot) return;
        slot.replaceChildren();

        const row = this.findLine(pending);
        if (row) {
          const image = row.querySelector('.cart-item__image');
          if (image) {
            const img = document.createElement('img');
            img.src = image.dataset.dhSrc || image.currentSrc || image.src;
            img.alt = '';
            img.width = 64;
            img.height = 80;
            img.className = 'dhrohar-bag-added__image';
            slot.append(img);
          }
          const text = document.createElement('div');
          const title = document.createElement('p');
          title.className = 'dhrohar-bag-added__title';
          title.textContent = row.querySelector('.cart-item__name')?.textContent.trim() || '';
          text.append(title);

          const options = Array.from(row.querySelectorAll('.cart-item__details dd'))
            .map((dd) => dd.textContent.replace(/,\s*$/, '').trim())
            .filter(Boolean);
          const quantity = row.querySelector('.quantity__input')?.value;
          const meta = document.createElement('p');
          meta.className = 'dhrohar-bag-added__meta';
          meta.textContent = [...options, quantity ? `Qty ${quantity}` : ''].filter(Boolean).join(' · ');
          if (meta.textContent) text.append(meta);

          const facts = row.querySelector('.dhrohar-line-facts');
          if (facts) text.append(facts.cloneNode(true));
          slot.append(text);
        }

        panel.hidden = false;

        const focusContinue = () => {
          setTimeout(() => {
            const button = panel.querySelector('[data-dhrohar-bag-continue]');
            if (button && !panel.hidden) button.focus();
          }, 50);
        };
        this.drawer.addEventListener('transitionend', focusContinue, { once: true });
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setTimeout(focusContinue, 100);
      }

      hidePanel() {
        const panel = this.container.querySelector('[data-dhrohar-bag-added]');
        if (panel) panel.hidden = true;
      }
    }
  );
})();

/* 0.6.0 (owner workbook B7): "Add to bag" on the empty-bag product picks (snippets/dhrohar-cart-picks.liquid).
   Same request and drawer update as the wishlist's add: Dawn re-renders the drawer and the header bag count, and
   the "Added to your bag" panel shows the new line. Errors (e.g. stock) are shown under the pick. */
(() => {
  if (window.DhroharCartPicks) return;
  window.DhroharCartPicks = true;

  const pubsub = () =>
    typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? { publish, events: PUB_SUB_EVENTS } : null;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest && event.target.closest('[data-dh-pick-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const pick = button.closest('[data-dh-pick]');
    const message = pick && pick.querySelector('[data-dh-pick-message]');
    const original = button.textContent;
    if (message) message.textContent = '';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    try {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { id: Number(button.dataset.dhPickAdd), quantity: 1 };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        const trigger = document.getElementById('cart-icon-bubble');
        if (typeof cart.setActiveElement === 'function' && trigger) cart.setActiveElement(trigger);
      }
      const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        if (message) message.textContent = state.description || state.message || 'This could not be added to your bag. Please try again.';
        return;
      }
      const ps = pubsub();
      if (ps && ps.events.cartUpdate) {
        try {
          ps.publish(ps.events.cartUpdate, { source: 'dhrohar-cart-picks', productVariantId: String(body.id), cartData: state });
        } catch (e) {
          /* ignore */
        }
      }
      const onCartPage = !!document.querySelector('cart-items:not(cart-drawer-items)');
      if (onCartPage) {
        window.location.reload();
        return;
      }
      if (cart && typeof cart.renderContents === 'function') {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(state);
        // The clicked button was replaced by the re-render: keep keyboard focus (and Escape) inside the bag.
        setTimeout(() => {
          const container = document.getElementById('CartDrawer');
          const inner = cart.querySelector('.drawer__inner');
          if (container && inner && typeof trapFocus === 'function') trapFocus(container, inner);
        }, 120);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (e) {
      if (message) message.textContent = 'This could not be added to your bag. Please try again.';
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = original;
      }
    }
  });
})();
